import type { QueryObserverResult } from '@tanstack/react-query'
import { HttpStatusCode } from 'axios'
import type { AxiosError } from 'axios'

import type { TCompradoresCadastrados } from '@/features/proposta/interfaces/proposta.type'
import type { CompradorPessoaJuridicaSchemaInput } from '@/features/proposta/schemas/compradores/comprador-pj.schema'
import type { TConta } from '@/shared/interfaces/conta.types'

export type FetchResult<TPatch> = { type: 'SUCCESS'; patch: TPatch } | { type: 'NOT_FOUND' } | { type: 'ERROR' }

export class CompradorPJUseCase {
  constructor(
    private readonly compradores: TCompradoresCadastrados,
    private readonly emailsJaVinculados: Record<string, string>,
    private readonly nacionalidadePrincipalId: string,
  ) {}

  private documentoIgual(cnpj: string, cnpjRaw: string): boolean {
    return cnpj.replace(/\D/g, '') === cnpjRaw.replace(/\D/g, '')
  }

  validarAvanco(cpf: string, buscouCnpj: boolean): { ok: boolean; error?: 'MISSING_CNPJ' | 'NEED_SEARCH' } {
    if (!cpf) return { ok: false, error: 'MISSING_CNPJ' }
    if (!buscouCnpj) return { ok: false, error: 'NEED_SEARCH' }
    return { ok: true }
  }

  interpretarResultadoBuscaManual(
    result: QueryObserverResult<TConta, AxiosError>,
  ): FetchResult<Partial<CompradorPessoaJuridicaSchemaInput>> {
    if (result.isError && result.error?.response?.status === HttpStatusCode.NotFound) return { type: 'NOT_FOUND' }
    if (result.isSuccess && result.data) {
      const conta = result.data
      const patch: CompradorPessoaJuridicaSchemaInput = {
        emailsJaVinculados: this.emailsJaVinculados,
        tipo: 'PessoaJuridica',
        mode: 'create',
        dados: {
          emailsJaVinculados: this.emailsJaVinculados,
          id: conta.id,
          cnpj: conta.cnpj,
          razaoSocial: conta.razaoSocial,
          buscouCnpj: !!conta.cnpj,
          email: conta.email ?? '',
          telefone: conta.telefone
            ? {
                numero: conta.telefone.numero,
                origem: conta.telefone.origem,
              }
            : {
                numero: '',
                origem: 'Brasil',
              },
        },
        responsavel: {
          tipoDoCliente: 'Contato',
          emailsJaVinculados: this.emailsJaVinculados,
          tipo: 'Representante',
          imagem: null,
          dados: {
            cpf: '',
            nome: '',
            profissaoId: '',
            nacionalidadeId: this.nacionalidadePrincipalId,
            maiorDe18Anos: 'sim',
            buscouCpf: false,
            email: null,
            id: '',
          },
          estadoCivil: {
            regimeCasamento: null,
            status: null,
            tipoAssinatura: undefined,
            uniaoEstavel: undefined,
          },
          contato: {
            email: '',
            telefone: {
              origem: 'Brasil',
              numero: '',
              principal: true,
            },
          },
        },
      }
      return { type: 'SUCCESS', patch }
    }
    return { type: 'ERROR' }
  }

  devePularBuscaPoisHaDuplicidade(cpfRaw: string): boolean {
    return this.compradores.some((c) => this.documentoIgual(c.documento, cpfRaw))
  }

  limparFormulario(): CompradorPessoaJuridicaSchemaInput {
    return {
      emailsJaVinculados: this.emailsJaVinculados,
      dados: {
        emailsJaVinculados: this.emailsJaVinculados,
        cnpj: '',
        razaoSocial: '',
        buscouCnpj: false,
        email: '',
        telefone: {
          numero: '',
          origem: 'Brasil',
        },
      },
      tipo: 'PessoaJuridica',
      mode: 'create',
      responsavel: {
        tipoDoCliente: 'Contato',
        dados: {
          cpf: '',
          nome: '',
          profissaoId: '',
          nacionalidadeId: this.nacionalidadePrincipalId,
          maiorDe18Anos: 'sim',
          buscouCpf: false,
          email: null,
          id: '',
        },
        estadoCivil: {
          status: null,
          regimeCasamento: null,
          uniaoEstavel: null,
          tipoAssinatura: undefined,
        },
        contato: {
          email: '',
          telefone: {
            origem: 'Brasil',
            numero: '',
            principal: true,
          },
        },
        imagem: null,
        emailsJaVinculados: this.emailsJaVinculados,
        tipo: 'Representante',
      },
    }
  }
}
